import React, { useState, useEffect } from 'react';
import { Calculator, Users, BookOpen, Percent } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const BlogPostReadRateCalculator = () => {
  const [totalVisitors, setTotalVisitors] = useState(1000);
  const [readersToEnd, setReadersToEnd] = useState(400);
  const [readRate, setReadRate] = useState(0);

  useEffect(() => {
    calculateReadRate();
  }, [totalVisitors, readersToEnd]);

  const calculateReadRate = () => {
    const rate = (readersToEnd / totalVisitors) * 100;
    setReadRate(parseFloat(rate.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/blog-post-read-rate-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Blog Post Read Rate Calculator: Boost Content Engagement | Free Tool</title>
        <meta name="description" content="Calculate your Blog Post Read Rate instantly. Compare with industry benchmarks, get content optimization tips, and improve reader engagement." />
        <meta name="keywords" content="blog read rate calculator, content engagement metrics, reader retention, blog analytics, content optimization" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Blog Read Rate Calculator: Optimize Content Engagement" />
        <meta property="og:description" content="Calculate your Blog Post Read Rate instantly. Get actionable insights to improve reader engagement and content performance." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Blog Read Rate Calculator: Optimize Content Engagement" />
        <meta property="twitter:description" content="Calculate your Blog Post Read Rate instantly. Get actionable insights to improve reader engagement and content performance." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Blog Post Read Rate Calculator",
            "description": "Free calculator to measure and optimize blog post engagement rates",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Read Rate",
              "Industry Benchmarks",
              "Content Optimization Tips",
              "Performance Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Blog Post Read Rate",
              "description": "The percentage of visitors who read a blog post to completion, measuring content engagement effectiveness."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Content Creators, Bloggers, Content Marketers"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Blog Post Read Rate",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter total number of visitors"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input number of readers who finished"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your read rate and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Blog Post Read Rate Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Visitors</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalVisitors}
                  onChange={(e) => setTotalVisitors(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="1000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Readers Who Finished</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <BookOpen className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={readersToEnd}
                  onChange={(e) => setReadersToEnd(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="400"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {readRate}%
            <Percent className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Blog Post Read Rate</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Blog Post Read Rate is {readRate}%. This means that {readRate}% of visitors who started reading your blog post finished it.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Read Rate Benchmarks by Content Type</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>How-to Articles: 55-65%</li>
          <li>List Posts: 50-60%</li>
          <li>Case Studies: 45-55%</li>
          <li>Industry News: 40-50%</li>
          <li>Long-form Content: 35-45%</li>
          <li>Opinion Pieces: 40-50%</li>
          <li>Tutorial Content: 60-70%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Content Optimization Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Write compelling headlines</li>
          <li>Use subheadings effectively</li>
          <li>Include relevant images</li>
          <li>Keep paragraphs short</li>
          <li>Use storytelling techniques</li>
          <li>Optimize readability</li>
          <li>Match search intent</li>
          <li>Create strong conclusions</li>
          <li>Add visual elements</li>
          <li>Improve formatting</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Readability Best Practices</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Use 2-3 sentences per paragraph</li>
          <li>Maintain 16px+ font size</li>
          <li>Ensure high color contrast</li>
          <li>Include white space</li>
          <li>Use bullet points</li>
          <li>Implement clear hierarchy</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Impact Factors</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Clear formatting increases read rate by 80%</li>
          <li>Relevant images boost completion by 30%</li>
          <li>Proper subheadings improve retention by 25%</li>
          <li>Mobile optimization increases reads by 35%</li>
          <li>Strong introductions lift completion by 20%</li>
        </ul>
      </div>
    </div>
  );
};

export default BlogPostReadRateCalculator;
